/**
 * @file useBlockLeaveGuard.ts
 * @description Защита несохранённого блока в конструкторе (phase19.16
 * Р1-6а; uiux-builder-shell.md §6 «уход с несохранённым блоком», §7 риск 7):
 * у редактора блока явное «Сохранить», и переход к другому блоку, разделу
 * или странице раньше терял правки молча.
 *
 * `guard()` спрашивает, только если правки есть: [Сохранить] — сохранить и
 * перейти (не сохранилось — остаться), [Не сохранять] — перейти,
 * [Остаться] — никуда не уходить. Ответ — через `choose` из окна.
 */

import { useCallback, useRef, useState } from "react";

export type LeaveChoice = "save" | "discard" | "stay";

export interface BlockLeaveGuard {
  /** Можно ли уходить; спрашивает человека, если есть правки. */
  guard: () => Promise<boolean>;
  /** Окно открыто. */
  asking: boolean;
  choose: (choice: LeaveChoice) => void;
}

export function useBlockLeaveGuard(dirty: boolean, save: () => Promise<boolean>): BlockLeaveGuard {
  const [asking, setAsking] = useState(false);
  const resolver = useRef<((choice: LeaveChoice) => void) | null>(null);
  const latest = useRef({ dirty, save });
  latest.current = { dirty, save };

  const guard = useCallback(async () => {
    if (!latest.current.dirty) return true;
    resolver.current?.("stay");
    setAsking(true);
    const choice = await new Promise<LeaveChoice>((resolve) => {
      resolver.current = resolve;
    });
    if (choice === "discard") return true;
    if (choice === "save") return latest.current.save();
    return false;
  }, []);

  const choose = useCallback((choice: LeaveChoice) => {
    resolver.current?.(choice);
    resolver.current = null;
    setAsking(false);
  }, []);

  return { guard, asking, choose };
}
